import React from "react";
import Pagination from "./Pagination";

type TableColumn = {
  key: string;
  header: string;
  align?: "left" | "center" | "right";
  accessor?: ({
    key,
    row,
    rowIndex,
  }: {
    key: string;
    row: any;
    rowIndex: number;
  }) => React.ReactNode;
  width?: string;
};

type TableData = Record<string, any>;

interface TableProps {
  columns?: Array<TableColumn>;
  data?: Array<TableData>;
  pagination?: {
    totalDocs: number;
    totalPages: number;
    currentPage: number;
    pageLimit: number;
    hasNextPage: number;
    hasPrevPage: number;
  } | null;
  className?: string;
  onPageChange?: (page: number) => void;
}

const TableV2: React.FC<TableProps> = ({
  columns = [],
  data = [],
  pagination = null,
  className = "",
  onPageChange,
}) => {
  return (
    <div className={className}>
      <div className="border rounded-xl overflow-hidden overflow-x-scroll">
        <table className="w-full text-sm text-left text-gray-700 min-w-max">
          {/* -------- Header -------- */}
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-3 px-3 font-medium whitespace-nowrap"
                  style={{ width: col?.width, textAlign: col?.align || "left" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* -------- Body -------- */}
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr
                  key={`row-${rowIndex}`}
                  className="border-b hover:bg-gray-50 transition"
                >
                  {columns.map((col, colIdx) => (
                    <td
                      key={`col-${colIdx}`}
                      className="py-2 px-3 align-middle"
                      style={{
                        width: col?.width,
                        textAlign: col?.align || "left",
                      }}
                    >
                      {col.accessor
                        ? col.accessor({ key: col.key, row, rowIndex })
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className="border-b">
                <td
                  colSpan={columns.length || 1}
                  className="py-4 px-3 text-center text-gray-500"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* -------- Pagination -------- */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          pageLimit={pagination.pageLimit}
          totalPages={pagination.totalPages}
          totalRecords={pagination.totalDocs}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default TableV2;
